import { parseContentUri } from "lib/actions/assets/parseContentUri";
import { AssetTypeAdapterInterface } from ".";
import { RenderableAsset } from "lib/types/assets/RenderableAsset";
import { AzureStorageResource } from "../AzureStorageResource";
import { IonResource, Resource } from "cesium";
import { Guard } from "lib/actions/util";
import TilesetPlugin from "components/elements/CesiumViewer/core/wrapper/TilesetPlugin";
import CameraPlugin from "components/elements/CesiumViewer/core/wrapper/CameraPlugin";
import TilesetStylingPlugin from "components/elements/CesiumViewer/core/wrapper/TilesetStylingPlugin";

export class TilesetAssetTypeAdapter implements AssetTypeAdapterInterface {
  /**
   * Render key to the id of the vector tileset loaded beside it. The only place
   * the pair is known: every caller still addresses the asset by its render key.
   */
  private readonly _vectorTilesetIds = new Map<string, string>();

  constructor(
    private readonly _tileset: TilesetPlugin,
    private readonly _camera: CameraPlugin,
    private readonly _styling: TilesetStylingPlugin,
  ) {}

  isExisting(uri: string) {
    return this._tileset.isExisting(uri);
  }

  isReady(uri: string) {
    return this._tileset.isReady(uri);
  }

  isShowing(uri: string) {
    return this.tilesetIdsFor(uri).some((id) => this._tileset.isShowing(id));
  }

  async load(version: RenderableAsset) {
    Guard.require(version, "version");
    if (
      version.asset.type !== "pointCloud" &&
      version.asset.type !== "model" &&
      version.asset.type !== "layer" &&
      version.asset.type !== "gisSimpleFeature"
    ) {
      throw new Error(
        `Unsupported asset type '${version.asset.type}', must be 'model', 'pointCloud', 'layer', or 'gisSimpleFeature'.`,
      );
    }
    // A vector-only asset carries the vector URI in both fields, so there is no
    // mesh tileset to load and loading the render key would fetch it twice.
    const isVectorOnly = version.vectorContentUri === version.contentUri;
    const vectorUri = version.vectorContentUri;

    if (!isVectorOnly) {
      const resource = await this.createResourceFromContentUri(
        version.contentUri,
        version.asset.projectId,
      );
      const isPointCloud = version.asset.type === "pointCloud";
      await this._tileset.load(version.contentUri, resource, { isPointCloud });
    }

    if (!vectorUri) return;

    try {
      const vectorResource = await this.createResourceFromContentUri(
        vectorUri,
        version.asset.projectId,
      );
      await this._tileset.load(vectorUri, vectorResource, {
        isVector: true,
        // Rules are aimed at the asset's render key, so without this a layer
        // criteria or paint rule would colour the geometry and leave the
        // linework at its own base colour.
        parentTilesetId: isVectorOnly ? undefined : version.contentUri,
        // GIS data is mostly 2D, and only lies on the ground once clamped.
        isDrapedUnlessSourceHasZ: version.asset.type === "gisSimpleFeature",
      });
      if (vectorUri !== version.contentUri) {
        this._vectorTilesetIds.set(version.contentUri, vectorUri);
      }
      // Every other caller of applyRules is a tool reacting to its own rule
      // change, so without this the tileset renders white at the default width.
      await this._styling.applyRules();
    } catch (error) {
      if (isVectorOnly) throw error;
      // The mesh tileset is already in the scene and the caller has no way to
      // undo that, so a missing vector tileset degrades the asset rather than
      // failing it.
      console.error(
        `Failed to load vector linework. ContentUri: ${version.contentUri}`,
        error,
      );
    }
  }

  show(uri: string) {
    this.tilesetIdsFor(uri).forEach((id) => this._tileset.show(id));
  }

  hide(uri: string) {
    this.tilesetIdsFor(uri).forEach((id) => this._tileset.hide(id));
  }

  async flyTo(uri: string) {
    Guard.require(uri, "uri");
    const tileset = this._tileset.get(uri);
    if (tileset) await this._camera.flyTo(tileset);
  }

  private tilesetIdsFor(uri: string): string[] {
    const vectorUri = this._vectorTilesetIds.get(uri);
    return vectorUri ? [uri, vectorUri] : [uri];
  }

  private async createResourceFromContentUri(
    contentUri: string,
    projectId: number,
  ): Promise<Resource> {
    const uri = parseContentUri(contentUri);
    if (uri.type !== "tileset") {
      throw new Error(
        `Invalid content URI type. Type '${uri.type}' must be a 'tileset'.`,
      );
    }

    let resource: Resource = null;
    if (uri.location === "ion") {
      const ionId = +uri.value;
      if (!ionId || Number.isNaN(ionId)) {
        throw new Error(
          `Invalid Cesium Ion id. Value '${uri.value}' must be a positive integer.`,
        );
      }
      resource = await IonResource.fromAssetId(ionId);
    } else if (uri.location === "self") {
      if (!uri.value.endsWith(".json")) {
        throw new Error(
          `Invalid content URI value. URL '${uri.value}' must be a .json tileset.`,
        );
      }
      resource = await AzureStorageResource.fromUrl(uri.value, projectId);
    } else {
      throw new Error(
        `Invalid content URI location. Location '${uri.location}' not supported.`,
      );
    }
    return resource;
  }
}
